import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { Lock, User, Loader2, ShieldCheck, ArrowLeft, KeyRound, CheckCircle2, Mail, Eye, EyeOff } from "lucide-react";
import PMRLogo from "../components/PMRLogo";
import { ADMIN_MOCK } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState("credentials");
  // credentials | otp | forgot-user | forgot-otp | forgot-newpass | forgot-done

  const [creds, setCreds] = useState({ username: "", password: "" });
  const [otp, setOtp] = useState("");
  const [resetUser, setResetUser] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (localStorage.getItem("pmr_admin") === "1") navigate("/admin", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const currentPass = localStorage.getItem("pmr_admin_pass") || ADMIN_MOCK.credentials.password;

  const submitCreds = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (creds.username === ADMIN_MOCK.credentials.username && creds.password === currentPass) {
        setStep("otp");
        setResendIn(30);
        toast({ title: "OTP sent", description: "6-digit code sent to admin phone/email." });
      } else {
        toast({ title: "Invalid credentials", description: "Please check username & password." });
      }
    }, 700);
  };

  const submitOtp = (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast({ title: "Enter 6-digit OTP" });
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (otp === ADMIN_MOCK.credentials.otp) {
        localStorage.setItem("pmr_admin", "1");
        toast({ title: "Welcome, Admin", description: "Signed in successfully." });
        navigate("/admin");
      } else {
        toast({ title: "Wrong OTP", description: "Check your code and try again." });
      }
    }, 700);
  };

  const sendResetOtp = (e) => {
    e.preventDefault();
    if (!resetUser) return toast({ title: "Enter username or email" });
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (resetUser.trim().toLowerCase() !== ADMIN_MOCK.credentials.username) {
        toast({ title: "Account not found", description: "This username isn't registered." });
        return;
      }
      setStep("forgot-otp");
      setResendIn(30);
      toast({ title: "Reset OTP sent", description: "Check the registered admin email/phone." });
    }, 800);
  };

  const verifyResetOtp = (e) => {
    e.preventDefault();
    if (resetOtp.length !== 6) return toast({ title: "Enter 6-digit OTP" });
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (resetOtp === ADMIN_MOCK.credentials.otp) {
        setStep("forgot-newpass");
      } else {
        toast({ title: "Wrong OTP", description: "Reset code doesn't match." });
      }
    }, 700);
  };

  const savePassword = (e) => {
    e.preventDefault();
    if (newPass.length < 6) return toast({ title: "Password too short", description: "Use at least 6 characters." });
    if (newPass !== confirmPass) return toast({ title: "Passwords don't match" });
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      localStorage.setItem("pmr_admin_pass", newPass);
      setStep("forgot-done");
      toast({ title: "Password updated", description: "You can now sign in with your new password." });
    }, 900);
  };

  const resendCode = () => {
    setResendIn(30);
    setOtp("");
    setResetOtp("");
    toast({ title: "OTP re-sent", description: "A new code was sent." });
  };

  const resetForgot = () => {
    setStep("credentials");
    setResetUser("");
    setResetOtp("");
    setNewPass("");
    setConfirmPass("");
  };

  const isForgot = step.startsWith("forgot");

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden flex items-center justify-center px-6 py-10">
      <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-teal-500/10 blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-orange-500/10 blur-3xl" />

      <div className="relative max-w-md w-full">
        <Link to="/" className="flex items-center gap-2 justify-center">
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-lg">
            <PMRLogo size={30} color="#0f766e" accent="#f97316" />
          </div>
          <div className="text-white">
            <div className="font-bold text-lg tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>PMR Travels</div>
            <div className="text-[10px] uppercase tracking-widest text-orange-400">Admin Console</div>
          </div>
        </Link>

        {/* Credentials */}
        {step === "credentials" && (
          <form onSubmit={submitCreds} className="mt-8 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 space-y-5">
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>Sign in to Dashboard</h1>
              <p className="mt-1 text-sm text-slate-400">Two-factor authentication protected</p>
            </div>

            <div>
              <Label htmlFor="un" className="text-slate-300">Username</Label>
              <div className="relative mt-1">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input id="un" value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} placeholder="admin"
                  className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-orange-400" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="pw" className="text-slate-300">Password</Label>
                <button type="button" onClick={() => setStep("forgot-user")} className="text-xs text-orange-400 hover:text-orange-300 font-semibold">
                  Forgot password?
                </button>
              </div>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input id="pw" type={showPass ? "text" : "password"} value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} placeholder="••••••••"
                  className="pl-9 pr-9 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-orange-400" />
                <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-11 bg-orange-500 hover:bg-orange-600">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : "Continue"}
            </Button>

            <div className="pt-3 border-t border-white/10 flex items-center gap-2 text-xs text-slate-400">
              <ShieldCheck className="w-3.5 h-3.5" />
              Demo credentials — <span className="font-mono text-slate-300">admin / pmr@2025</span>
            </div>
          </form>
        )}

        {/* OTP */}
        {step === "otp" && (
          <form onSubmit={submitOtp} className="mt-8 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 space-y-5">
            <button type="button" onClick={() => { setStep("credentials"); setOtp(""); }} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white">
              <ArrowLeft className="w-3 h-3" /> Change credentials
            </button>
            <div>
              <div className="w-12 h-12 rounded-xl bg-orange-500/20 text-orange-400 flex items-center justify-center mb-3"><KeyRound className="w-5 h-5" /></div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>Enter OTP</h1>
              <p className="mt-1 text-sm text-slate-400">A 6-digit code was sent to the admin's registered mobile number.</p>
            </div>
            <div className="flex justify-center py-2">
              <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="bg-white/5 text-white border-white/20 w-11 h-12 text-lg" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button type="submit" disabled={loading || otp.length !== 6} className="w-full h-11 bg-orange-500 hover:bg-orange-600">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : "Verify & Sign in"}
            </Button>
            <div className="text-center text-xs text-slate-400">
              {resendIn > 0 ? (<>Resend code in <span className="text-white font-semibold">{resendIn}s</span></>) : (
                <button type="button" onClick={resendCode} className="text-orange-400 hover:text-orange-300 font-semibold">Resend OTP</button>
              )}
            </div>
            <div className="pt-3 border-t border-white/10 flex items-center gap-2 text-xs text-slate-400">
              <ShieldCheck className="w-3.5 h-3.5" /> Demo OTP — <span className="font-mono text-slate-300">142536</span>
            </div>
          </form>
        )}

        {/* Forgot - Username */}
        {step === "forgot-user" && (
          <form onSubmit={sendResetOtp} className="mt-8 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 space-y-5">
            <button type="button" onClick={resetForgot} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white">
              <ArrowLeft className="w-3 h-3" /> Back to sign in
            </button>
            <div>
              <div className="w-12 h-12 rounded-xl bg-teal-500/20 text-teal-300 flex items-center justify-center mb-3"><Mail className="w-5 h-5" /></div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>Forgot password?</h1>
              <p className="mt-1 text-sm text-slate-400">Enter your admin username to receive a password-reset OTP on the registered email & phone.</p>
            </div>
            <div>
              <Label htmlFor="resetUser" className="text-slate-300">Admin username</Label>
              <div className="relative mt-1">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input id="resetUser" value={resetUser} onChange={(e) => setResetUser(e.target.value)} placeholder="admin"
                  className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-orange-400" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-orange-500 hover:bg-orange-600">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending reset code...</> : "Send reset OTP"}
            </Button>
          </form>
        )}

        {/* Forgot - OTP */}
        {step === "forgot-otp" && (
          <form onSubmit={verifyResetOtp} className="mt-8 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 space-y-5">
            <button type="button" onClick={() => setStep("forgot-user")} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white">
              <ArrowLeft className="w-3 h-3" /> Change username
            </button>
            <div>
              <div className="w-12 h-12 rounded-xl bg-orange-500/20 text-orange-400 flex items-center justify-center mb-3"><KeyRound className="w-5 h-5" /></div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>Enter Reset OTP</h1>
              <p className="mt-1 text-sm text-slate-400">We've sent a 6-digit code to the admin's registered email & phone.</p>
            </div>
            <div className="flex justify-center py-2">
              <InputOTP maxLength={6} value={resetOtp} onChange={setResetOtp}>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="bg-white/5 text-white border-white/20 w-11 h-12 text-lg" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button type="submit" disabled={loading || resetOtp.length !== 6} className="w-full h-11 bg-orange-500 hover:bg-orange-600">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : "Verify OTP"}
            </Button>
            <div className="text-center text-xs text-slate-400">
              {resendIn > 0 ? (<>Resend code in <span className="text-white font-semibold">{resendIn}s</span></>) : (
                <button type="button" onClick={resendCode} className="text-orange-400 hover:text-orange-300 font-semibold">Resend OTP</button>
              )}
            </div>
            <div className="pt-3 border-t border-white/10 flex items-center gap-2 text-xs text-slate-400">
              <ShieldCheck className="w-3.5 h-3.5" /> Demo OTP — <span className="font-mono text-slate-300">142536</span>
            </div>
          </form>
        )}

        {/* Forgot - New Password */}
        {step === "forgot-newpass" && (
          <form onSubmit={savePassword} className="mt-8 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 space-y-5">
            <div>
              <div className="w-12 h-12 rounded-xl bg-teal-500/20 text-teal-300 flex items-center justify-center mb-3"><Lock className="w-5 h-5" /></div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>Set a new password</h1>
              <p className="mt-1 text-sm text-slate-400">Minimum 6 characters. Use a mix of letters, numbers & symbols for a strong password.</p>
            </div>
            <div>
              <Label htmlFor="newpass" className="text-slate-300">New password</Label>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input id="newpass" type={showPass ? "text" : "password"} value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="••••••••"
                  className="pl-9 pr-9 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-orange-400" />
                <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirmpass" className="text-slate-300">Confirm password</Label>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input id="confirmpass" type={showPass ? "text" : "password"} value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} placeholder="••••••••"
                  className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-orange-400" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-orange-500 hover:bg-orange-600">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</> : "Update password"}
            </Button>
          </form>
        )}

        {/* Forgot - Done */}
        {step === "forgot-done" && (
          <div className="mt-8 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <h1 className="mt-5 text-2xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>Password updated!</h1>
            <p className="mt-2 text-sm text-slate-400">You can now sign in with your new password.</p>
            <Button onClick={resetForgot} className="mt-6 w-full h-11 bg-orange-500 hover:bg-orange-600">Back to Sign In</Button>
            <p className="mt-4 text-[11px] text-orange-400 font-semibold">DEMO MODE — New password is stored in browser (localStorage). Not persistent across devices.</p>
          </div>
        )}

        <div className="mt-6 text-center text-xs text-slate-500 space-x-3">
          <Link to="/" className="hover:text-white">← Back to website</Link>
          <span>·</span>
          <Link to="/agent/login" className="hover:text-white">Agent login</Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
